import { useEffect, useState } from 'react'
import { useTranslation } from '@/i18n'
import { apiService, type Interview } from '@/service/api'

const Placeholder = ({ text }: { text: string }) => (
  <p className="text-sm text-muted-foreground italic">{text}</p>
)

interface Props {
  interview: Interview
  onSwitchToDocuments?: () => void
}

const OverviewTab = ({ interview, onSwitchToDocuments }: Props) => {
  const { t } = useTranslation()
  const summary = interview.meeting_summary?.trim()
  const actionItems = (interview.meeting_action_items ?? []).filter((s) => s?.trim())

  const [docCount, setDocCount] = useState<number | null>(null)
  const [docPreview, setDocPreview] = useState<string[]>([])
  const [docsError, setDocsError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const docs = await apiService.listInterviewDocuments(interview.id)
        if (cancelled) return
        setDocCount(docs.length)
        setDocPreview(docs.slice(0, 3).map((d) => d.document_name))
        setDocsError(null)
      } catch (err) {
        if (cancelled) return
        setDocsError(err instanceof Error ? err.message : 'Failed to load documents')
        setDocCount(0)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [interview.id])

  return (
    <div className="flex flex-col gap-8 max-w-[760px]">
      {/* Topic Description — backend has no dedicated field; reuse meeting_summary when present */}
      <section className="flex flex-col gap-2">
        <h2 className="text-base font-semibold text-foreground">
          {t('hek_topic:overview.description.title', { defaultValue: 'Topic Description' })}
        </h2>
        {summary ? (
          <p className="text-sm text-foreground leading-relaxed whitespace-pre-line">{summary}</p>
        ) : (
          <Placeholder
            text={t('hek_topic:overview.description.placeholder', {
              defaultValue: 'No description yet. The AI will generate a summary after the interviews are processed.',
            })}
          />
        )}
      </section>

      {/* Key Objective — backed by meeting_action_items (LLM-extracted post-call) */}
      <section className="flex flex-col gap-2">
        <h2 className="text-base font-semibold text-foreground">
          {t('hek_topic:overview.objective.title', { defaultValue: 'Key Objective' })}
        </h2>
        {actionItems.length === 0 ? (
          <Placeholder
            text={t('hek_topic:overview.objective.placeholder', {
              defaultValue: 'Key objectives will appear here once the interviews are scored and synthesized.',
            })}
          />
        ) : (
          <ul className="flex flex-col gap-1.5 list-disc pl-5">
            {actionItems.map((item, i) => (
              <li key={i} className="text-sm text-foreground leading-relaxed">
                {item}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Relevant Documents and Data — surfaced from interview_documents */}
      <section className="flex flex-col gap-2">
        <h2 className="text-base font-semibold text-foreground">
          {t('hek_topic:overview.docs.title', { defaultValue: 'Relevant Documents and Data' })}
        </h2>
        {docsError ? (
          <Placeholder text={docsError} />
        ) : docCount === null ? (
          <Placeholder
            text={t('hek_topic:overview.docs.loading', { defaultValue: 'Loading documents…' })}
          />
        ) : docCount === 0 ? (
          <Placeholder
            text={t('hek_topic:overview.docs.placeholder', {
              defaultValue: 'Attached documents and supporting data will surface here once linked to this topic.',
            })}
          />
        ) : (
          <div className="flex flex-col gap-2">
            <p className="text-sm text-foreground">
              {t('hek_topic:overview.docs.count', {
                count: docCount,
                defaultValue: '{{count}} document attached',
                defaultValue_plural: '{{count}} documents attached',
              })}
            </p>
            {docPreview.length > 0 && (
              <ul className="flex flex-col gap-1 list-disc pl-5">
                {docPreview.map((name) => (
                  <li key={name} className="text-sm text-muted-foreground truncate max-w-[680px]">
                    {name}
                  </li>
                ))}
              </ul>
            )}
            {onSwitchToDocuments && (
              <button
                type="button"
                onClick={onSwitchToDocuments}
                className="self-start text-sm text-sky-600 hover:text-sky-700 hover:underline"
              >
                {t('hek_topic:overview.docs.view_all', { defaultValue: 'View all documents →' })}
              </button>
            )}
          </div>
        )}
      </section>
    </div>
  )
}

export default OverviewTab
